// src/components/LearnerList.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  User, 
  School, 
  Phone, 
  Mail,
  Calendar,
  Users,
  Plus,
  Download,
  Eye,
  Grid3X3,
  List,
  ArrowUpDown,
  ArrowLeft,
  Building2,
  GraduationCap,
  MessageSquarePlus,
  Send,
  Paperclip,
  X,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Clock,
  AlertCircle,
  CheckCircle2,
  Star,
  BookOpen,
  Heart,
  Activity,
  FileText,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { LearnerService } from '../../services/learnerService';
import { LearnerUpdateService } from '../../services/learnerUpdateService';
import ProfileImage from './ProfileImage';
import { useAuth } from '../../contexts/AuthContext';

// Update Categories Configuration
const UPDATE_CATEGORIES = [
  { value: 'general', label: 'General', icon: MessageSquarePlus, color: 'bg-slate-500' },
  { value: 'academic', label: 'Academic', icon: BookOpen, color: 'bg-blue-500' },
  { value: 'attendance', label: 'Attendance', icon: Calendar, color: 'bg-purple-500' },
  { value: 'behavior', label: 'Behavior', icon: Activity, color: 'bg-amber-500' },
  { value: 'achievement', label: 'Achievement', icon: Star, color: 'bg-emerald-500' },
  { value: 'concern', label: 'Concern', icon: AlertCircle, color: 'bg-rose-500' },
  { value: 'health', label: 'Health', icon: Heart, color: 'bg-pink-500' }
];

// Priority Configuration
const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'text-slate-500' },
  { value: 'normal', label: 'Normal', color: 'text-blue-500' },
  { value: 'high', label: 'High', color: 'text-amber-500' },
  { value: 'urgent', label: 'Urgent', color: 'text-rose-500' }
];

// Format relative time
const formatRelativeTime = (date) => {
  if (!date) return '';
  const now = new Date();
  const diff = now - new Date(date);
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(date).toLocaleDateString();
};

// Single Update Item Component
const UpdateItem = ({ update, onDelete }) => {
  const category = UPDATE_CATEGORIES.find(c => c.value === update.category) || UPDATE_CATEGORIES[0];
  const CategoryIcon = category.icon;
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="group relative bg-white/60 backdrop-blur-sm rounded-xl p-4 border border-slate-200/60 hover:border-slate-300 transition-all duration-200 hover:shadow-sm">
      {/* Category Badge */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className={`${category.color} p-1.5 rounded-lg`}>
            <CategoryIcon className="h-3.5 w-3.5 text-white" />
          </span>
          <span className="text-xs font-medium text-slate-600">{category.label}</span>
          {update.priority === 'high' && (
            <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">High Priority</span>
          )}
          {update.priority === 'urgent' && (
            <span className="px-2 py-0.5 text-xs font-medium bg-rose-100 text-rose-700 rounded-full animate-pulse">Urgent</span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatRelativeTime(update.createdAt)}
            {update.isEdited && <span className="italic">(edited)</span>}
          </span>
          
          {/* Actions Menu */}
          <div className="relative">
            <button 
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-slate-100 transition-all"
            >
              <MoreVertical className="h-4 w-4 text-slate-400" />
            </button>
            
            {showMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20">
                  <button 
                    onClick={() => { onDelete(update.id); setShowMenu(false); }}
                    className="w-full px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50 flex items-center gap-2"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">{update.content}</p>
      
      {/* Attachments */}
      {update.attachments && update.attachments.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {update.attachments.map((attachment, idx) => (
            <a
              key={idx}
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs text-slate-600 transition-colors"
            >
              {attachment.fileType?.startsWith('image/') ? (
                <ImageIcon className="h-3.5 w-3.5" />
              ) : (
                <FileText className="h-3.5 w-3.5" />
              )}
              <span className="max-w-[120px] truncate">{attachment.fileName}</span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// Update Form Component
const UpdateForm = ({ learnerId, onSubmit, onCancel, isSubmitting }) => {
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('normal');
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    
    onSubmit({
      content: content.trim(),
      category,
      priority
    }, attachments);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files].slice(0, 5)); // Max 5 files
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gradient-to-br from-slate-50 to-slate-100/80 rounded-xl p-4 border border-slate-200">
      {/* Category & Priority Row */}
      <div className="flex flex-wrap gap-3 mb-3">
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Category</label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          >
            {UPDATE_CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-slate-500 mb-1.5">Priority</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
          >
            {PRIORITIES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Content Textarea */}
      <div className="mb-3">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Write an update about this learner..."
          rows={3}
          className="w-full px-3 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none placeholder:text-slate-400"
        />
      </div>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {attachments.map((file, idx) => (
            <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs">
              <FileText className="h-3.5 w-3.5 text-slate-400" />
              <span className="max-w-[100px] truncate text-slate-600">{file.name}</span>
              <button type="button" onClick={() => removeAttachment(idx)} className="text-slate-400 hover:text-rose-500">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Actions Row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 text-slate-500 hover:text-slate-700 hover:bg-white rounded-lg transition-colors"
            title="Attach files"
          >
            <Paperclip className="h-4 w-4" />
          </button>
          <span className="text-xs text-slate-400">{attachments.length}/5 files</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-white rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!content.trim() || isSubmitting}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg flex items-center gap-2 transition-colors"
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Post Update
          </button>
        </div>
      </div>
    </form>
  );
};

// Learner Card Component with Update Panel
const LearnerCard = ({ 
  learner, 
  onView, 
  onEdit, 
  onDelete,
  isExpanded,
  onToggleExpand
}) => {
  const [updates, setUpdates] = useState([]);
  const [loadingUpdates, setLoadingUpdates] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [updateCount, setUpdateCount] = useState(0);

  // Load updates when card expands
  useEffect(() => {
    if (isExpanded) {
      loadUpdates();
    }
  }, [isExpanded, learner.id]);

  const loadUpdates = async () => {
    try {
      setLoadingUpdates(true);
      const data = await LearnerUpdateService.getUpdates(learner.id);
      setUpdates(data);
      setUpdateCount(data.length);
    } catch (error) {
      console.error('Error loading updates:', error);
    } finally {
      setLoadingUpdates(false);
    }
  };

  const handleSubmitUpdate = async (updateData, attachments) => {
    try {
      setIsSubmitting(true);
      await LearnerUpdateService.createUpdate(learner.id, updateData, attachments);
      setShowUpdateForm(false);
      await loadUpdates();
    } catch (error) {
      console.error('Error creating update:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUpdate = async (updateId) => {
    if (!window.confirm('Are you sure you want to delete this update?')) return;
    
    try {
      await LearnerUpdateService.deleteUpdate(updateId);
      setUpdates(prev => prev.filter(u => u.id !== updateId));
      setUpdateCount(prev => prev - 1);
    } catch (error) {
      console.error('Error deleting update:', error);
    }
  };

  const fullName = `${learner.surname || learner.learnerSurname || ''}, ${learner.firstName || learner.learnerName || ''} ${learner.secondName || ''}`.trim();

  return (
    <div className={`bg-slate-100 rounded-2xl border border-slate-200 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden ${isExpanded ? 'ring-2 ring-blue-500/20' : ''}`}>
      {/* Card Header with Actions */}
      <div className="p-4">
        <div className="flex justify-end gap-1 mb-2">
          <button
            onClick={() => onView(learner)}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            title="View Details"
          >
            <Eye className="h-4 w-4" />
          </button>
          <button
            onClick={() => onEdit(learner)}
            className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Edit"
          >
            <Edit3 className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(learner.id)}
            className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>

        {/* Learner Info */}
        <div className="flex items-start gap-4">
          <div className="relative">
            <ProfileImage
              src={learner.profileImageUrl}
              alt={fullName}
              size="lg"
            />
            {updateCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-blue-500 text-white text-xs font-semibold rounded-full flex items-center justify-center">
                {updateCount > 99 ? '99+' : updateCount}
              </span>
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">{fullName}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
              <span className="text-sm text-slate-500">{learner.gender || 'N/A'}</span>
              {learner.grade && (
                <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                  <GraduationCap className="h-3.5 w-3.5" />
                  Grade {learner.grade}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Learner Details Grid */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <span className="truncate">{learner.identityNumber || 'No ID Number'}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            <School className="h-4 w-4 text-slate-400 flex-shrink-0" />
            <span className="truncate">{learner.schoolName || learner.school || 'No School'}</span>
          </div>
          
          {learner.projectName && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Building2 className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="truncate">{learner.projectName}</span>
            </div>
          )}
          
          {learner.mobileNumber && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span>{learner.mobileNumber}</span>
            </div>
          )}
          
          {learner.email && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="truncate">{learner.email}</span>
            </div>
          )}
          
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Registered {learner.createdAt instanceof Date ? 
              learner.createdAt.toLocaleDateString() : 
              (learner.createdAt ? new Date(learner.createdAt).toLocaleDateString() : 'N/A')
            }</span>
          </div>
        </div>
      </div>

      {/* Expand Toggle Button */}
      <button
        onClick={() => onToggleExpand(learner.id)}
        className="w-full px-4 py-3 bg-white border-t border-slate-200/60 flex items-center justify-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 hover:from-slate-100 hover:to-slate-150/80 transition-all"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span>Updates {updateCount > 0 && `(${updateCount})`}</span>
        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>

      {/* Expanded Updates Panel */}
      {isExpanded && (
        <div className="border-t border-slate-200/60 bg-gradient-to-b from-slate-50/50 to-white">
          <div className="p-4 space-y-4">
            {/* Add Update Button / Form */}
            {showUpdateForm ? (
              <UpdateForm
                learnerId={learner.id}
                onSubmit={handleSubmitUpdate}
                onCancel={() => setShowUpdateForm(false)}
                isSubmitting={isSubmitting}
              />
            ) : (
              <button
                onClick={() => setShowUpdateForm(true)}
                className="w-full px-4 py-3 border-2 border-dashed border-slate-300 hover:border-blue-400 rounded-xl text-sm font-medium text-slate-500 hover:text-blue-600 flex items-center justify-center gap-2 transition-all hover:bg-blue-50/50"
              >
                <Plus className="h-4 w-4" />
                Add Update
              </button>
            )}

            {/* Updates Feed */}
            <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
              {loadingUpdates ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 text-slate-400 animate-spin" />
                </div>
              ) : updates.length > 0 ? (
                updates.map(update => (
                  <UpdateItem 
                    key={update.id} 
                    update={update} 
                    onDelete={handleDeleteUpdate}
                  />
                ))
              ) : (
                <div className="text-center py-8">
                  <MessageSquarePlus className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No updates yet</p>
                  <p className="text-xs text-slate-400 mt-1">Be the first to add an update</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main LearnerList Component
const LearnerList = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [learners, setLearners] = useState([]);
  const [filteredLearners, setFilteredLearners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    grade: '',
    school: '',
    gender: '',
    project: ''
  });
  const [viewMode, setViewMode] = useState('cards');
  const [sortBy, setSortBy] = useState('surname');
  const [sortOrder, setSortOrder] = useState('asc');
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Load learners on component mount
  useEffect(() => {
    loadLearners();
  }, []);

  // Filter learners when search term or filters change
  useEffect(() => {
    filterLearners();
  }, [learners, searchTerm, filters, sortBy, sortOrder]);

  const loadLearners = async () => {
    try {
      setLoading(true);
      const data = await LearnerService.getLearners();
      setLearners(data);
    } catch (err) {
      setError(err.message || 'Failed to load learners');
    } finally {
      setLoading(false);
    }
  };

  const filterLearners = () => {
    let filtered = [...learners];

    // Search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(learner => {
        const surname = learner.surname || learner.learnerSurname || '';
        const firstName = learner.firstName || learner.learnerName || '';
        const secondName = learner.secondName || '';
        const identityNumber = learner.identityNumber || '';
        const schoolName = learner.schoolName || learner.school || '';
        const projectName = learner.projectName || learner.project || '';
        
        return surname.toLowerCase().includes(term) ||
          firstName.toLowerCase().includes(term) ||
          secondName.toLowerCase().includes(term) ||
          identityNumber.toLowerCase().includes(term) ||
          schoolName.toLowerCase().includes(term) ||
          projectName.toLowerCase().includes(term);
      });
    }

    // Apply filters
    if (filters.grade) {
      filtered = filtered.filter(learner => learner.grade === filters.grade);
    }
    if (filters.school) {
      filtered = filtered.filter(learner => 
        (learner.schoolName || learner.school) === filters.school
      );
    }
    if (filters.gender) {
      filtered = filtered.filter(learner => learner.gender === filters.gender);
    }
    if (filters.project) {
      filtered = filtered.filter(learner => learner.projectName === filters.project);
    }

    // Sort
    filtered.sort((a, b) => {
      const aVal = a[sortBy] || a[`learner${sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}`] || '';
      const bVal = b[sortBy] || b[`learner${sortBy.charAt(0).toUpperCase() + sortBy.slice(1)}`] || '';
      return sortOrder === 'asc' 
        ? aVal.localeCompare(bVal) 
        : bVal.localeCompare(aVal);
    });

    setFilteredLearners(filtered);
  };

  // Get unique values for filter dropdowns
  const uniqueGrades = [...new Set(learners.map(l => l.grade).filter(Boolean))].sort();
  const uniqueSchools = [...new Set(learners.map(l => l.schoolName || l.school).filter(Boolean))].sort();
  const uniqueProjects = [...new Set(learners.map(l => l.projectName).filter(Boolean))].sort();

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

   const handleEdit = (learner) => {
    navigate(`/learners/edit/${learner.id}`, { state: { learner } });
  };

  const handleDelete = async (learnerId) => {
    if (window.confirm('Are you sure you want to delete this learner?')) {
      try {
        await LearnerService.deleteLearner(learnerId);
        await loadLearners();
      } catch (err) {
        setError(err.message || 'Failed to delete learner');
      }
    }
  };

  const handleViewDetails = (learner) => {
    navigate(`/learners/detail/${learner.id}`, { state: { learner } });
  };

  const toggleCardExpand = (learnerId) => {
    setExpandedCardId(prev => prev === learnerId ? null : learnerId);
  };

  const exportToCSV = () => {
    const headers = ['Surname', 'First Name', 'Second Name', 'Gender', 'Grade', 'ID Number', 'School', 'Project', 'Mobile', 'Email'];
    const rows = filteredLearners.map(l => [
      l.surname || l.learnerSurname || '',
      l.firstName || l.learnerName || '',
      l.secondName || '',
      l.gender || '',
      l.grade || '',
      l.identityNumber || '',
      l.schoolName || l.school || '',
      l.projectName || '',
      l.mobileNumber || '',
      l.email || ''
    ]);
    
    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `learners_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilters({ grade: '', school: '', gender: '', project: '' });
    setSearchTerm('');
  };

  const hasActiveFilters = filters.grade || filters.school || filters.gender || filters.project || searchTerm;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading learners...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                    <Users className="h-6 w-6 text-white" />
                  </div>
                  Learner Management
                </h1>
                <p className="text-sm text-slate-500 mt-0.5">
                  {filteredLearners.length} of {learners.length} learners
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={exportToCSV}
                disabled={filteredLearners.length === 0}
                className="px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white text-sm font-medium rounded-md flex items-center gap-2 transition-colors shadow-sm"
              >
                <Download className="h-4 w-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
              <button
                onClick={() => navigate('/learners/register')}
                className="px-4 py-2.5 bg-primary-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md flex items-center gap-2 transition-colors shadow-sm"
              >
                <Plus className="h-4 w-4" />
                <span className="hidden sm:inline">Add Learner</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Search and Filters Bar */}
        <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, ID, school, project..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 focus:bg-white transition-all"
              />
            </div>

            {/* Filter Toggle & View Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-2.5 text-sm font-medium rounded-xl flex items-center gap-2 transition-all ${
                  showFilters || hasActiveFilters
                    ? 'bg-blue-50 text-blue-600 border border-blue-200'
                    : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-1 px-1.5 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                    {[filters.grade, filters.school, filters.gender, filters.project, searchTerm].filter(Boolean).length}
                  </span>
                )}
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2.5 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                >
                  Clear
                </button>
              )}

              {/* View Mode Toggle */}
              <div className="flex bg-slate-100 rounded-xl p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'cards' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <Grid3X3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'table' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-200/60">
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Grade</label>
                <select
                  value={filters.grade}
                  onChange={(e) => setFilters(prev => ({ ...prev, grade: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                >
                  <option value="">All Grades</option>
                  {uniqueGrades.map(grade => (
                    <option key={grade} value={grade}>Grade {grade}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">School</label>
                <select
                  value={filters.school}
                  onChange={(e) => setFilters(prev => ({ ...prev, school: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                >
                  <option value="">All Schools</option>
                  {uniqueSchools.map(school => (
                    <option key={school} value={school}>{school}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Gender</label>
                <select
                  value={filters.gender}
                  onChange={(e) => setFilters(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                >
                  <option value="">All Genders</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1.5">Project</label>
                <select
                  value={filters.project}
                  onChange={(e) => setFilters(prev => ({ ...prev, project: e.target.value }))}
                  className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                >
                  <option value="">All Projects</option>
                  {uniqueProjects.map(project => (
                    <option key={project} value={project}>{project}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p>{error}</p>
            <button onClick={() => setError(null)} className="ml-auto p-1 hover:bg-rose-100 rounded-lg">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Content */}
        {filteredLearners.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200/60">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No learners found</h3>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              {learners.length === 0 
                ? "No learners have been registered yet. Add your first learner to get started." 
                : "No learners match your current search criteria. Try adjusting your filters."
              }
            </p>
            <button
              onClick={() => navigate('/learners/register')}
              className="px-6 py-3 bg-primary-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors inline-flex items-center gap-2"
            >
              <Plus className="h-5 w-5" />
              Register First Learner
            </button>
          </div>
        ) : viewMode === 'cards' ? (
          /* Card View */
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6 items-start">
            {filteredLearners.map((learner) => (
              <LearnerCard
                key={learner.id}
                learner={learner}
                onView={handleViewDetails}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isExpanded={expandedCardId === learner.id}
                onToggleExpand={toggleCardExpand}
              />
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Learner</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Gender</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Grade</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">School</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLearners.map((learner) => (
                    <tr key={learner.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <ProfileImage
                            src={learner.profileImageUrl}
                            alt={`${learner.firstName || learner.learnerName || ''} ${learner.surname || learner.learnerSurname || ''}`}
                            size="sm"
                          />
                          <div>
                            <div className="font-medium text-slate-900">
                              {(learner.surname || learner.learnerSurname || '')}, {(learner.firstName || learner.learnerName || '')}
                            </div>
                            <div className="text-sm text-slate-500">{learner.identityNumber || 'No ID'}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600">{learner.gender || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{learner.grade ? `Grade ${learner.grade}` : 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{learner.schoolName || learner.school || 'N/A'}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{learner.projectName || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewDetails(learner)}
                            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(learner)}
                            className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            <Edit3 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(learner.id)}
                            className="p-2 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default LearnerList;